import { createAsyncThunk, createSelector, createSlice } from '@reduxjs/toolkit'
import axios from 'axios'
import moment from 'moment-timezone'
import {
  trafficCartsReportUrl,
  trafficUniqueFieldValuesUrl,
  trafficMetricsUrl,
  googleAdsReportingUrl,
  googleAdsMetricsUrl,
  googleAdsMetricsSummaryUrl,
  fbAdsMetricsSummaryUrl,
  klaviyoAdsMetricsSummaryUrl,
  pinterestAdsMetricsSummaryUrl,
  tiktokAdsMetricsSummaryUrl,
  amazonAdsMetricsSummaryUrl,
  unmatchedAdsMetricsSummaryUrl,
  mmmSummaryUrl,
  mmmSimulateUrl,
  mmmTrainStatusUrl,
  preferencesUrl,
} from '../../modules/urls'
import { getAxiosDefaultConfig } from '@/modules/utils'
import { DEFAULT_TIME_PERIOD } from './constants'
import { set } from 'lodash'
import { setPage } from '@/actions/trafficActions'
import { buildGaqlQuery } from './utils'

export const initialState = {
  trafficReport: {},
  currentTab: 'metrics',
  tabPagination: {},
  tabTotalPages: {},
  metric: {},
  googleAdsReporting: {},
  googleAdsMetricsSummary: {},
  googleAdsMetrics: {},
  googleAdsAuditData: {},
  googleAdsApiError: false,
  fbAdsMetricsSummary: {},
  fbAdsAuditData: {},
  fbAdsApiError: false,
  klaviyoAdsMetricsSummary: {},
  klaviyoAdsAuditData: {},
  klaviyoApiError: false,
  pinterestAdsMetricsSummary: {},
  pinterestAdsAuditData: {},
  pinterestApiError: false,
  tiktokAdsMetricsSummary: {},
  tiktokAdsAuditData: {},
  tiktokApiError: false,
  amazonAdsMetricsSummary: {},
  amazonAdsAuditData: {},
  amazonApiError: false,
  unmatchedAdsMetricsSummary: {},
  uniqueFieldValues: {},
  selectedRows: {},
  metricTableColumns: [],
  metricTableRawData: [],
  metricTableRows: [],
  // Marketing Mix Modeling (MMM) state
  mmmData: {
    status: 'idle',
    jobId: undefined,
    progress: 0,
    data: undefined,
    source: undefined,
    error: undefined,
  },
  mmmSimulation: {},
}

const timePeriodToMinutes = (timePeriod) => {
  if (timePeriod === 'today') return 'today'
  if (timePeriod === 'yesterday') return 'yesterday'

  const timePeriodPair = timePeriod.split('-')
  return moment.duration(timePeriodPair[0], timePeriodPair[1]).asMinutes()
}

export const fetchUniqueFieldValues = createAsyncThunk('trafficReport/fetchUniqueFieldValues', async (payload) => {
  const { fieldName, timePeriod, timezone, customDateRange } = payload
  let reqUrl = `${trafficUniqueFieldValuesUrl}/${fieldName}/?timePeriod=${encodeURIComponent(
    timePeriod
  )}&timezone=${encodeURIComponent(timezone)}`
  if (customDateRange?.start) {
    reqUrl += `&start=${encodeURIComponent(customDateRange.start)}`
  }
  if (customDateRange?.end) {
    reqUrl += `&end=${encodeURIComponent(customDateRange.end)}`
  }
  const res = await axios.get(reqUrl, getAxiosDefaultConfig())
  return res?.data
})

/**
 * Map the active Traffic tab name to the reportKey used in trafficconfig
 * (which mirrors the dashboard's `dashboard_config.columns[reportKey]` shape).
 */
export const tabNameToReportKey = (tabName: string): string => {
  switch (tabName) {
    case 'sessions': return 'trafficSession'
    case 'visitors': return 'trafficVisitor'
    case 'carts': return 'trafficCart'
    case 'checkouts': return 'trafficCheckout'
    case 'orders': return 'trafficOrder'
    default: return ''
  }
}

/**
 * Pull the active "field dir" string from a saved trafficconfig for the given tab.
 * Returns undefined if no column has a sort set.
 */
const sortFromTrafficConfig = (trafficconfig: any, tabName: string): string | undefined => {
  const reportKey = tabNameToReportKey(tabName)
  if (!reportKey) return undefined
  const cols = trafficconfig?.columns?.[reportKey]
  if (!cols) return undefined
  for (const colName of Object.keys(cols)) {
    const dir = cols[colName]?.sort
    if (dir === 'asc' || dir === 'desc') {
      return `${colName} ${dir}`
    }
  }
  return undefined
}

export const fetchTrafficReport = createAsyncThunk(
  'trafficReport/fetchTrafficReport',
  async (payload, { getState, dispatch }) => {
    const { timePeriod = DEFAULT_TIME_PERIOD, customDateRange, tabName, pagination = { pageIndex: 1, pageSize: 20 }, filters, sort: sortOverride } = payload

    const timestamp = new Date().getTime()
    const timeQuery = timePeriodToMinutes(timePeriod)

    const page = pagination.pageIndex
    const limit = pagination.pageSize
    const {
      user: { default_timezone: timezone, default_currency: currency, trafficconfig },
    } = getState().root
    const sort = sortOverride || sortFromTrafficConfig(trafficconfig, tabName)

    let requestUrl = `${trafficCartsReportUrl}?timestamp=${timestamp}&page=${page}&limit=${limit}&timezone=${timezone}&currency=${currency}`

    if (tabName) {
      requestUrl += `&tab=${tabName}`
    }

    const todayTimestamp = moment.tz(timezone).hour(0).minute(0).second(0).millisecond(0).toDate().getTime()

    // Handle custom date range
    if (timePeriod === 'custom' && customDateRange && customDateRange.start && customDateRange.end) {
      const startTimestamp = moment.tz(customDateRange.start, timezone).hour(0).minute(0).second(0).millisecond(0).toDate().getTime()
      const endTimestamp = moment.tz(customDateRange.end, timezone).hour(23).minute(59).second(59).millisecond(999).toDate().getTime()
      requestUrl += `&start_timestamp=${startTimestamp}&end_timestamp=${endTimestamp}&today_timestamp=${todayTimestamp}&by_day=true`
    } else if (timeQuery === 'today') {
      requestUrl += `&start_timestamp=${todayTimestamp}&today_timestamp=${todayTimestamp}&by_day=true`
    } else if (timeQuery === 'yesterday') {
      const startTimestamp = (moment.tz(timezone).hour(0).minute(0).second(0).millisecond(0).subtract(1, 'day').toDate()).getTime()
      const endTimestamp = (moment.tz(timezone).hour(23).minute(59).second(59).millisecond(999).subtract(1, 'day').toDate()).getTime()
      requestUrl += `&start_timestamp=${startTimestamp}&end_timestamp=${endTimestamp}&today_timestamp=${todayTimestamp}&by_day=true`
    } else {
      requestUrl += `&today_timestamp=${todayTimestamp}&min_ago=${timeQuery}`
    }

    const res = await axios.post(requestUrl, { filters, sort }, getAxiosDefaultConfig())

    // Also update the tabPagination state
    if (tabName) {
      dispatch(setTabPagination({ [tabName]: pagination }))
    }

    return res?.data
  }
)

/**
 * Persist the user's traffic columns config and update Redux. Mirrors the
 * dashboard saveConfig pattern. After save, callers should dispatch
 * fetchTrafficReport so the active tab reflects any sort changes.
 */
export const saveTrafficConfig = createAsyncThunk(
  'trafficReport/saveTrafficConfig',
  async (config: any, { getState, dispatch }) => {
    const { email } = (getState() as any).root.user
    await axios.post(`${preferencesUrl}/traffic_config`, { email, config }, getAxiosDefaultConfig())
    dispatch({ type: 'UPDATE_USER_ROOT', payload: { trafficconfig: config } })
    return config
  }
)

export const fetchTrafficMetric = createAsyncThunk(
  'trafficReport/fetchTrafficMetric',
  async (payload, { getState }) => {
    const { metric, timePeriod = DEFAULT_TIME_PERIOD, customDateRange, startTimestamp, endTimestamp, filters, groupBy } = payload

    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const reqUrl = `${trafficMetricsUrl}/${metric}`

    let _startTimestamp = startTimestamp
    let _endTimestamp = endTimestamp

    // Handle custom date range
    if (timePeriod === 'custom' && customDateRange && customDateRange.start && customDateRange.end) {
      _startTimestamp = moment.tz(customDateRange.start, timezone).hour(0).minute(0).second(0).millisecond(0).toDate().getTime()
      _endTimestamp = moment.tz(customDateRange.end, timezone).hour(23).minute(59).second(59).millisecond(999).toDate().getTime()
    }

    const reqPayload = {
      timePeriod,
      startTimestamp: _startTimestamp,
      endTimestamp: _endTimestamp,
      filters,
      groupBy,
      timezone,
      currency,
    }

    const res = await axios.post(reqUrl, reqPayload, getAxiosDefaultConfig())

    return res?.data

  }
)

export const fetchGoogleAdsReporting = createAsyncThunk(
  'trafficReport/fetchGoogleAdsReporting',
  async (payload, { getState }) => {
    const {
      user: { default_timezone: timezone },
    } = getState().root

    const { select, from, where, limit, timePeriod = DEFAULT_TIME_PERIOD } = payload

    const query = buildGaqlQuery({ select, from, where, limit, timePeriod })

    const reqUrl = `${googleAdsReportingUrl}`
    const res = await axios.post(reqUrl, { query }, getAxiosDefaultConfig())
    return res?.data
  }
)

export const fetchGoogleAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchGoogleAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${googleAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )
    // console.log('fetchGoogleAdsMetricsSummary res?.data', res?.data)

    // Set the audit data
    const auditData = res?.data?.auditData
    if (auditData) {
      dispatch(setGoogleAdsAuditData(auditData))
    }

    const apiError = res?.data?.apiError
    if (apiError) {
      dispatch(setGoogleAdsApiError(apiError))
    }

    return res?.data?.mergedData
  }
)

export const fetchGoogleAdsMetrics = createAsyncThunk(
  'trafficReport/fetchGoogleAdsMetrics',
  async (payload, { getState }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, filters, groupBy, limit = 20, attribution } = payload

    const reqUrl = `${googleAdsMetricsUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        timezone,
        filters,
        groupBy,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )
    return res?.data
  }
)

export const fetchFbAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchFbAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${fbAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency,
      },
      getAxiosDefaultConfig()
    )
    // console.log('fetchFbAdsMetricsSummary res?.data', res?.data)

    // Set the audit data
    const auditData = res?.data?.auditData
    if (auditData) {
      dispatch(setFbAdsAuditData(auditData))
    }

    const apiError = res?.data?.apiError
    if (apiError) {
      dispatch(setFbAdsApiError(apiError))
    }

    return res?.data?.mergedData
  }
)

export const fetchKlaviyoAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchKlaviyoAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${klaviyoAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )
    // console.log('fetchFbAdsMetricsSummary res?.data', res?.data)

    // Set the audit data
    const auditData = res?.data?.auditData
    if (auditData) {
      dispatch(setKlaviyoAdsAuditData(auditData))
    }

    const apiError = res?.data?.apiError
    if (apiError) {
      dispatch(setKlaviyoApiError(apiError))
    }

    return res?.data?.mergedData
  }
)

export const fetchPinterestAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchPinterestAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${pinterestAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )
    // console.log('fetchPinterestAdsMetricsSummary res?.data', res?.data)

    // Set the audit data
    const auditData = res?.data?.auditData
    if (auditData) {
      dispatch(setPinterestAdsAuditData(auditData))
    }

    const apiError = res?.data?.apiError
    if (apiError) {
      dispatch(setPinterestApiError(apiError))
    }

    return res?.data?.mergedData
  }
)

export const fetchTiktokAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchTiktokAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${tiktokAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )
    // console.log('fetchTiktokAdsMetricsSummary res?.data', res?.data)

    // Set the audit data
    const auditData = res?.data?.auditData
    if (auditData) {
      dispatch(setTiktokAdsAuditData(auditData))
    }

    const apiError = res?.data?.apiError
    if (apiError) {
      dispatch(setTiktokApiError(apiError))
    }

    return res?.data?.mergedData
  }
)

export const fetchAmazonAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchAmazonAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${amazonAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )
    // console.log('fetchAmazonAdsMetricsSummary res?.data', res?.data)

    // Set the audit data
    const auditData = res?.data?.auditData
    if (auditData) {
      dispatch(setAmazonAdsAuditData(auditData))
    }

    const apiError = res?.data?.apiError
    if (apiError) {
      dispatch(setAmazonApiError(apiError))
    }

    return res?.data?.mergedData
  }
)

export const fetchUnmatchedAdsMetricsSummary = createAsyncThunk(
  'trafficReport/fetchUnmatchedAdsMetricsSummary',
  async (payload, { getState, dispatch }) => {
    const {
      user: { default_timezone: timezone, default_currency: currency },
    } = getState().root

    const { timePeriod, customDateRange, filters, limit = 20, attribution } = payload

    const reqUrl = `${unmatchedAdsMetricsSummaryUrl}`
    const res = await axios.post(
      reqUrl,
      {
        timePeriod,
        customDateRange,
        timezone,
        filters,
        limit,
        attribution,
        currency
      },
      getAxiosDefaultConfig()
    )

    return res?.data?.mergedData
  }
)

/**
 * Marketing Mix Modeling (MMM) async thunks
 */
export const fetchMMMSummary = createAsyncThunk(
  'trafficReport/fetchMMMSummary',
  async (payload: { timePeriod?: string; customDateRange?: { start?: string; end?: string }; useCampaigns?: boolean; domain?: string } = {}, { getState, rejectWithValue }) => {
    const {
      user: { default_currency: currency },
    } = (getState() as any).root

    const { timePeriod, customDateRange, useCampaigns, domain } = payload
    try {
      const res = await axios.post(
        mmmSummaryUrl,
        { currency, timePeriod, customDateRange, useCampaigns, domain },
        getAxiosDefaultConfig()
      )
      return res?.data
    } catch (err: any) {
      let errorMessage: string = 'Unable to load Marketing Mix data'
      if (err.response?.data?.error) {
        errorMessage = err.response.data.error
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ENOTFOUND') {
        errorMessage = 'Cannot connect to Marketing Mix service. Please check your connection or contact support.'
      } else if (err.code === 'ETIMEDOUT' || err.message?.includes('timeout')) {
        errorMessage = 'Request timed out. The Marketing Mix service may be slow or unavailable. Please try again.'
      } else if (err.response?.status === 500) {
        errorMessage = 'Marketing Mix service error. Please try again later or contact support.'
      } else if (err.response?.status === 404) {
        errorMessage = 'Marketing Mix model not found. Training will start automatically.'
      } else if (err.message) {
        errorMessage = err.message.includes('Network Error')
          ? 'Network error. Please check your connection and try again.'
          : err.message
      }
      return rejectWithValue(errorMessage)
    }
  }
)

export const fetchMMMTrainStatus = createAsyncThunk(
  'trafficReport/fetchMMMTrainStatus',
  async (jobId: string, { rejectWithValue }) => {
    try {
      const res = await axios.get(mmmTrainStatusUrl(jobId), getAxiosDefaultConfig())
      return res?.data
    } catch (_err) {
      return rejectWithValue('Status check failed, will retry')
    }
  }
)

export const fetchMMMSimulation = createAsyncThunk(
  'trafficReport/fetchMMMSimulation',
  async (payload: { allocations: Record<string, number> }, { getState }) => {
    const {
      user: { default_currency: currency },
    } = (getState() as any).root
    const { allocations } = payload
    const res = await axios.post(mmmSimulateUrl, { allocations, currency }, getAxiosDefaultConfig())
    return res?.data
  }
)

export const trafficReportSlice = createSlice({
  name: 'trafficReport',
  initialState,
  reducers: {
    setCurrentTab: (state, action) => {
      state.currentTab = action.payload
    },
    setTabPagination: (state, action) => {
      state.tabPagination = { ...state.tabPagination, ...action.payload }
    },
    setTabTotalPages: (state, action) => {
      state.tabTotalPages = action.payload
    },
    setSelectedRows: (state, action) => {
      // For the metric data table row selection
      state.selectedRows = action.payload
    },
    resetFetchTrafficReportStatus: (state) => {
      state.trafficReport.status = 'idle'
    },
    resetFetchTrafficMetricsStatus: (state) => {
      state.metric.status = 'idle'
    },
    setMetricTableColumns: (state, action) => {
      state.metricTableColumns = action.payload
    },
    setMetricTableRawData: (state, action) => {
      state.metricTableRawData = action.payload
    },
    setMetricTableRows: (state, action) => {
      state.metricTableRows = action.payload
    },
    setGoogleAdsAuditData: (state, action) => {
      state.googleAdsAuditData = action.payload
    },
    setGoogleAdsApiError: (state, action) => {
      state.googleAdsApiError = action.payload
    },
    resetFetchGoogleAdsMetricsSummaryStatus: (state) => {
      state.googleAdsMetricsSummary.status = 'idle'
    },
    setFbAdsAuditData: (state, action) => {
      state.fbAdsAuditData = action.payload
    },
    setFbAdsApiError: (state, action) => {
      state.fbAdsApiError = action.payload
    },
    resetFetchFbAdsMetricsSummaryStatus: (state) => {
      state.fbAdsMetricsSummary.status = 'idle'
    },
    setKlaviyoAdsAuditData: (state, action) => {
      state.klaviyoAdsAuditData = action.payload
    },
    setKlaviyoApiError: (state, action) => {
      state.klaviyoApiError = action.payload
    },
    resetFetchKlaviyoAdsMetricsSummaryStatus: (state) => {
      state.klaviyoAdsMetricsSummary.status = 'idle'
    },
    setPinterestAdsAuditData: (state, action) => {
      state.pinterestAdsAuditData = action.payload
    },
    setPinterestApiError: (state, action) => {
      state.pinterestApiError = action.payload
    },
    resetFetchPinterestAdsMetricsSummaryStatus: (state) => {
      state.pinterestAdsMetricsSummary.status = 'idle'
    },
    setTiktokAdsAuditData: (state, action) => {
      state.tiktokAdsAuditData = action.payload
    },
    setTiktokApiError: (state, action) => {
      state.tiktokApiError = action.payload
    },
    resetFetchTiktokAdsMetricsSummaryStatus: (state) => {
      state.tiktokAdsMetricsSummary.status = 'idle'
    },
    setAmazonAdsAuditData: (state, action) => {
      state.amazonAdsAuditData = action.payload
    },
    setAmazonApiError: (state, action) => {
      state.amazonApiError = action.payload
    },
    resetFetchAmazonAdsMetricsSummaryStatus: (state) => {
      state.amazonAdsMetricsSummary.status = 'idle'
    },
    resetFetchUnmatchedAdsMetricsSummaryStatus: (state) => {
      state.unmatchedAdsMetricsSummary.status = 'idle'
    },
    // MMM reducers
    resetFetchMMMSummaryStatus: (state) => {
      state.mmmData.status = 'idle'
    },
    resetMMMSimulation: (state) => {
      state.mmmSimulation = {}
    },
  },
  extraReducers(builder) {
    builder
      /**
       *
       * unique field values api
       *
       */
      .addCase(fetchUniqueFieldValues.pending, (state, action) => {
        const { fieldName, timePeriod } = action.meta.arg
        const stateKey = `${fieldName}--${timePeriod}`
        state.uniqueFieldValues[stateKey] = state.uniqueFieldValues[stateKey] || {}
        state.uniqueFieldValues[stateKey].status = 'loading'
      })
      .addCase(fetchUniqueFieldValues.fulfilled, (state, action) => {
        const { fieldName, timePeriod } = action.meta.arg
        const stateKey = `${fieldName}--${timePeriod}`
        state.uniqueFieldValues[stateKey] = state.uniqueFieldValues[stateKey] || {}
        state.uniqueFieldValues[stateKey].data = action.payload
        state.uniqueFieldValues[stateKey].status = 'succeeded'
      })
      .addCase(fetchUniqueFieldValues.rejected, (state, action) => {
        const { fieldName, timePeriod } = action.meta.arg
        const stateKey = `${fieldName}--${timePeriod}`
        state.uniqueFieldValues[stateKey] = state.uniqueFieldValues[stateKey] || {}
        state.uniqueFieldValues[stateKey].status = 'failed'
        state.uniqueFieldValues[stateKey].error = action.error.message
      })

      /**
       *
       * traffic reports api
       *
       */
      .addCase(fetchTrafficReport.pending, (state, action) => {
        state.trafficReport.status = 'loading'
      })
      .addCase(fetchTrafficReport.fulfilled, (state, action) => {
        const { tabName } = action.meta.arg

        if (tabName) {
          state.trafficReport.data = {
            ...state.trafficReport.data,
            [tabName]: action.payload[tabName],
          }
        } else {
          state.trafficReport.data = action.payload
        }
        state.trafficReport.status = 'succeeded'
      })
      .addCase(fetchTrafficReport.rejected, (state, action) => {
        state.trafficReport.status = 'failed'
        state.trafficReport.error = action.error.message
      })

      /**
       *
       * metric api
       *
       */
      .addCase(fetchTrafficMetric.pending, (state, action) => {
        state.metric.status = 'loading'
      })
      .addCase(fetchTrafficMetric.fulfilled, (state, action) => {
        state.metric.data = action.payload
        state.metric.status = 'succeeded'
      })
      .addCase(fetchTrafficMetric.rejected, (state, action) => {
        state.metric.status = 'failed'
        state.metric.error = action.error.message
      })

      /**
       *
       * google ads api
       *
       */
      .addCase(fetchGoogleAdsReporting.pending, (state, action) => {
        state.googleAdsReporting.status = 'loading'
      })
      .addCase(fetchGoogleAdsReporting.fulfilled, (state, action) => {
        state.googleAdsReporting.data = action.payload
        state.googleAdsReporting.status = 'succeeded'
      })
      .addCase(fetchGoogleAdsReporting.rejected, (state, action) => {
        state.googleAdsReporting.status = 'failed'
        state.googleAdsReporting.error = action.error.message
      })

      /**
       *
       * Google ads metrics summary api
       *
       */
      .addCase(fetchGoogleAdsMetricsSummary.pending, (state, action) => {
        state.googleAdsMetricsSummary.status = 'loading'
      })
      .addCase(fetchGoogleAdsMetricsSummary.fulfilled, (state, action) => {
        state.googleAdsMetricsSummary.data = action.payload
        state.googleAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchGoogleAdsMetricsSummary.rejected, (state, action) => {
        state.googleAdsMetricsSummary.status = 'failed'
        state.googleAdsMetricsSummary.error = action.error.message
      })

      /**
       *
       * Google ads metrics api
      *
      */
     .addCase(fetchGoogleAdsMetrics.pending, (state, action) => {
       state.googleAdsMetrics.status = 'loading'
      })
      .addCase(fetchGoogleAdsMetrics.fulfilled, (state, action) => {
        state.googleAdsMetrics.data = action.payload
        state.googleAdsMetrics.status = 'succeeded'
      })
      .addCase(fetchGoogleAdsMetrics.rejected, (state, action) => {
        state.googleAdsMetrics.status = 'failed'
        state.googleAdsMetrics.error = action.error.message
      })

      /**
       *
       * FB ads metrics summary api
       *
       */
      .addCase(fetchFbAdsMetricsSummary.pending, (state, action) => {
        state.fbAdsMetricsSummary.status = 'loading'
      })
      .addCase(fetchFbAdsMetricsSummary.fulfilled, (state, action) => {
        state.fbAdsMetricsSummary.data = action.payload
        state.fbAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchFbAdsMetricsSummary.rejected, (state, action) => {
        state.fbAdsMetricsSummary.status = 'failed'
        state.fbAdsMetricsSummary.error = action.error.message
      })

      /**
       *
       * Klaviyo ads metrics summary api
       *
       * */
      .addCase(fetchKlaviyoAdsMetricsSummary.pending, (state, action) => {
        state.klaviyoAdsMetricsSummary.status = 'loading'})
      .addCase(fetchKlaviyoAdsMetricsSummary.fulfilled, (state, action) => {
        state.klaviyoAdsMetricsSummary.data = action.payload
        state.klaviyoAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchKlaviyoAdsMetricsSummary.rejected, (state, action) => {
        state.klaviyoAdsMetricsSummary.status = 'failed'
        state.klaviyoAdsMetricsSummary.error = action.error.message
      })

      /**
       *
       * Pinterest ads metrics summary api
       *
       * */
      .addCase(fetchPinterestAdsMetricsSummary.pending, (state, action) => {
        state.pinterestAdsMetricsSummary.status = 'loading'})
      .addCase(fetchPinterestAdsMetricsSummary.fulfilled, (state, action) => {
        state.pinterestAdsMetricsSummary.data = action.payload
        state.pinterestAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchPinterestAdsMetricsSummary.rejected, (state, action) => {
        state.pinterestAdsMetricsSummary.status = 'failed'
        state.pinterestAdsMetricsSummary.error = action.error.message
      })

      /**
       *
       * TikTok ads metrics summary api
       *
       * */
      .addCase(fetchTiktokAdsMetricsSummary.pending, (state, action) => {
        state.tiktokAdsMetricsSummary.status = 'loading'})
      .addCase(fetchTiktokAdsMetricsSummary.fulfilled, (state, action) => {
        state.tiktokAdsMetricsSummary.data = action.payload
        state.tiktokAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchTiktokAdsMetricsSummary.rejected, (state, action) => {
        state.tiktokAdsMetricsSummary.status = 'failed'
        state.tiktokAdsMetricsSummary.error = action.error.message
      })

      /**
       *
       * Amazon ads metrics summary api
       *
       * */
      .addCase(fetchAmazonAdsMetricsSummary.pending, (state, action) => {
        state.amazonAdsMetricsSummary.status = 'loading'})
      .addCase(fetchAmazonAdsMetricsSummary.fulfilled, (state, action) => {
        state.amazonAdsMetricsSummary.data = action.payload
        state.amazonAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchAmazonAdsMetricsSummary.rejected, (state, action) => {
        state.amazonAdsMetricsSummary.status = 'failed'
        state.amazonAdsMetricsSummary.error = action.error.message
      })

      /**
       * Unmatched ads metrics summary api
       *
       */
      .addCase(fetchUnmatchedAdsMetricsSummary.pending, (state, action) => {
        state.unmatchedAdsMetricsSummary.status = 'loading'
      })
      .addCase(fetchUnmatchedAdsMetricsSummary.fulfilled, (state, action) => {
        state.unmatchedAdsMetricsSummary.data = action.payload
        state.unmatchedAdsMetricsSummary.status = 'succeeded'
      })
      .addCase(fetchUnmatchedAdsMetricsSummary.rejected, (state, action) => {
        state.unmatchedAdsMetricsSummary.status = 'failed'
        state.unmatchedAdsMetricsSummary.error = action.error.message
      })

      /**
       * Marketing Mix Modeling (MMM) api
       */
      .addCase(fetchMMMSummary.pending, (state) => {
        state.mmmData.status = 'loading'
        state.mmmData.jobId = undefined
        state.mmmData.progress = 0
      })
      .addCase(fetchMMMSummary.fulfilled, (state, action) => {
        if (action.payload?.status === 'training') {
          state.mmmData.status = 'training'
          state.mmmData.jobId = action.payload.jobId
          state.mmmData.data = undefined
          state.mmmData.progress = 0
        } else {
          state.mmmData.data = action.payload?.data
          state.mmmData.source = action.payload?.source
          state.mmmData.status = 'succeeded'
          state.mmmData.jobId = undefined
          state.mmmData.progress = 100
        }
      })
      .addCase(fetchMMMSummary.rejected, (state, action) => {
        state.mmmData.status = 'failed'
        state.mmmData.error = (action.payload as string) || action.error?.message
        state.mmmData.jobId = undefined
      })
      .addCase(fetchMMMTrainStatus.fulfilled, (state, action) => {
        if (action.payload?.progress !== undefined) {
          state.mmmData.progress = action.payload.progress
        }
        if (action.payload?.status === 'completed') {
          state.mmmData.status = 'idle'
          state.mmmData.jobId = undefined
          state.mmmData.progress = 100
        } else if (action.payload?.status === 'failed') {
          state.mmmData.status = 'failed'
          state.mmmData.error = action.payload?.error || 'Training failed'
          state.mmmData.jobId = undefined
          state.mmmData.progress = 0
        }
      })
      .addCase(fetchMMMTrainStatus.rejected, () => {
        // Keep polling on transient errors
      })
      .addCase(fetchMMMSimulation.pending, (state) => {
        state.mmmSimulation.status = 'loading'
      })
      .addCase(fetchMMMSimulation.fulfilled, (state, action) => {
        state.mmmSimulation.data = action.payload?.data
        state.mmmSimulation.status = 'succeeded'
      })
      .addCase(fetchMMMSimulation.rejected, (state, action) => {
        state.mmmSimulation.status = 'failed'
        state.mmmSimulation.error = action.error.message
      })
  },
})

export const {
  setCurrentTab,
  setTabPagination,
  setTabTotalPages,
  setSelectedRows,
  resetFetchTrafficReportStatus,
  resetFetchTrafficMetricsStatus,
  setMetricTableColumns,
  setMetricTableRawData,
  setMetricTableRows,
  setGoogleAdsAuditData,
  setGoogleAdsApiError,
  resetFetchGoogleAdsMetricsSummaryStatus,
  setFbAdsAuditData,
  setFbAdsApiError,
  resetFetchFbAdsMetricsSummaryStatus,
  setKlaviyoAdsAuditData,
  setKlaviyoApiError,
  resetFetchKlaviyoAdsMetricsSummaryStatus,
  setPinterestAdsAuditData,
  setPinterestApiError,
  resetFetchPinterestAdsMetricsSummaryStatus,
  setTiktokAdsAuditData,
  setTiktokApiError,
  resetFetchTiktokAdsMetricsSummaryStatus,
  setAmazonAdsAuditData,
  setAmazonApiError,
  resetFetchAmazonAdsMetricsSummaryStatus,
  resetFetchUnmatchedAdsMetricsSummaryStatus,
  // MMM exports
  resetFetchMMMSummaryStatus,
  resetMMMSimulation,
} = trafficReportSlice.actions

/**
 *
 * Selectors
 *
 */
export const selectTrafficUniqueFieldValuesStatus = createSelector(
  [(state) => state.trafficReport.uniqueFieldValues, (state, { fieldName, timePeriod }) => ({ fieldName, timePeriod })],
  (uniqueFieldValues, { fieldName, timePeriod }) => {
    const stateKey = `${fieldName}--${timePeriod}`
    return uniqueFieldValues[stateKey]?.status ? uniqueFieldValues[stateKey].status : 'idle'
  }
)
export const selectTrafficUniqueFieldValues = createSelector(
  [(state) => state.trafficReport.uniqueFieldValues, (state, { fieldName, timePeriod }) => ({ fieldName, timePeriod })],
  (uniqueFieldValues, { fieldName, timePeriod }) => {
    const stateKey = `${fieldName}--${timePeriod}`
    return uniqueFieldValues[stateKey] ? uniqueFieldValues[stateKey].data : null
  }
)

export const selectMetricTableColumns = (state) => state.trafficReport.metricTableColumns
export const selectMetricTableRawData = (state) => state.trafficReport.metricTableRawData
export const selectMetricTableRows = (state) => state.trafficReport.metricTableRows

export const selectTrafficReport = (state) => state.trafficReport.trafficReport.data
export const selectTrafficReportStatus = (state) => state.trafficReport.trafficReport.status || 'idle'
export const selectTrafficReportError = (state) => state.trafficReport.trafficReport.error

export const selectTrafficMetric = (state) => state.trafficReport.metric.data
export const selectTrafficMetricStatus = (state) => state.trafficReport.metric.status || 'idle'
export const selectTrafficMetricError = (state) => state.trafficReport.metric.error

export const selectGoogleAdsReporting = (state) => state.trafficReport.googleAdsReporting.data
export const selectGoogleAdsReportingStatus = (state) => state.trafficReport.googleAdsReporting.status || 'idle'
export const selectGoogleAdsReportingError = (state) => state.trafficReport.googleAdsReporting.error

export const selectGoogleAdsMetricsSummary = (state) => state.trafficReport.googleAdsMetricsSummary.data
export const selectGoogleAdsMetricsSummaryStatus = (state) =>
  state.trafficReport.googleAdsMetricsSummary.status || 'idle'
export const selectGoogleAdsMetricsSummaryError = (state) => state.trafficReport.googleAdsMetricsSummary.error
export const selectGoogleAdsAuditData = (state) => state.trafficReport.googleAdsAuditData
export const selectGoogleAdsApiError = (state) => state.trafficReport.googleAdsApiError

export const selectGoogleAdsMetrics = (state) => state.trafficReport.googleAdsMetrics.data
export const selectGoogleAdsMetricsStatus = (state) => state.trafficReport.googleAdsMetrics.status || 'idle'
export const selectGoogleAdsMetricsError = (state) => state.trafficReport.googleAdsMetrics.error

export const selectFbAdsMetricsSummary = (state) => state.trafficReport.fbAdsMetricsSummary.data
export const selectFbAdsMetricsSummaryStatus = (state) =>
  state.trafficReport.fbAdsMetricsSummary.status || 'idle'
export const selectFbAdsMetricsSummaryError = (state) => state.trafficReport.fbAdsMetricsSummary.error
export const selectFbAdsAuditData = (state) => state.trafficReport.fbAdsAuditData
export const selectFbAdsApiError = (state) => state.trafficReport.fbAdsApiError

export const selectKlaviyoAdsMetricsSummary = (state) => state.trafficReport.klaviyoAdsMetricsSummary.data
export const selectKlaviyoAdsMetricsSummaryStatus = (state) =>
  state.trafficReport.klaviyoAdsMetricsSummary.status || 'idle'
export const selectKlaviyoAdsMetricsSummaryError = (state) => state.trafficReport.klaviyoAdsMetricsSummary.error
export const selectKlaviyoAdsAuditData = (state) => state.trafficReport.klaviyoAdsAuditData
export const selectKlaviyoApiError = (state) => state.trafficReport.klaviyoApiError

export const selectPinterestAdsMetricsSummary = (state) => state.trafficReport.pinterestAdsMetricsSummary.data
export const selectPinterestAdsMetricsSummaryStatus = (state) =>
  state.trafficReport.pinterestAdsMetricsSummary.status || 'idle'
export const selectPinterestAdsMetricsSummaryError = (state) => state.trafficReport.pinterestAdsMetricsSummary.error
export const selectPinterestAdsAuditData = (state) => state.trafficReport.pinterestAdsAuditData
export const selectPinterestApiError = (state) => state.trafficReport.pinterestApiError

export const selectTiktokAdsMetricsSummary = (state) => state.trafficReport.tiktokAdsMetricsSummary.data
export const selectTiktokAdsMetricsSummaryStatus = (state) =>
  state.trafficReport.tiktokAdsMetricsSummary.status || 'idle'
export const selectTiktokAdsMetricsSummaryError = (state) => state.trafficReport.tiktokAdsMetricsSummary.error
export const selectTiktokAdsAuditData = (state) => state.trafficReport.tiktokAdsAuditData
export const selectTiktokApiError = (state) => state.trafficReport.tiktokApiError

export const selectAmazonAdsMetricsSummary = (state) => state.trafficReport.amazonAdsMetricsSummary.data
export const selectAmazonAdsMetricsSummaryStatus = (state) =>
  state.trafficReport.amazonAdsMetricsSummary.status || 'idle'
export const selectAmazonAdsMetricsSummaryError = (state) => state.trafficReport.amazonAdsMetricsSummary.error
export const selectAmazonAdsAuditData = (state) => state.trafficReport.amazonAdsAuditData
export const selectAmazonApiError = (state) => state.trafficReport.amazonApiError

export const selectUnmatchedAdsMetricsSummary = (state) => state.trafficReport.unmatchedAdsMetricsSummary.data
export const selectUnmatchedAdsMetricsSummaryStatus = (state) => state.trafficReport.unmatchedAdsMetricsSummary.status || 'idle'
export const selectUnmatchedAdsMetricsSummaryError = (state) => state.trafficReport.unmatchedAdsMetricsSummary.error

// MMM Selectors
export const selectMMMData = (state) => state.trafficReport.mmmData.data
export const selectMMMDataStatus = (state) => state.trafficReport.mmmData.status || 'idle'
export const selectMMMDataError = (state) => state.trafficReport.mmmData.error
export const selectMMMDataSource = (state) => state.trafficReport.mmmData.source
export const selectMMMJobId = (state) => state.trafficReport.mmmData.jobId
export const selectMMMProgress = (state) => state.trafficReport.mmmData.progress || 0
export const selectMMMSimulation = (state) => state.trafficReport.mmmSimulation.data
export const selectMMMSimulationStatus = (state) => state.trafficReport.mmmSimulation.status || 'idle'

export const selectCurrentTab = (state) => state.trafficReport.currentTab
export const selectTabPagination = createSelector(
  [(state) => state.trafficReport.tabPagination, (state, tabName) => tabName],
  (tabPagination, tabName) => tabPagination[tabName] || null
)
export const selectTabTotalPages = (state) => state.trafficReport.tabTotalPages
export const selectSelectedRows = (state) => state.trafficReport.selectedRows

export default trafficReportSlice.reducer
